import * as React from "react"
import { CheckCircle2, ExternalLink } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useBookingT } from "@/hooks/use-translations"
import type { ConnectGuide } from "@/features/integrations/api/types"

type Props = {
  guide: ConnectGuide
  fullDocsUrl?: string | null
}

export function ConnectGuideSection({ guide, fullDocsUrl }: Props) {
  const t = useBookingT()
  if (guide.prerequisites.length === 0 && guide.steps.length === 0) return null

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              {t("integrations_setup_guide_title", "How to connect")}
            </h2>
            <p className="text-sm text-neutral-600">
              {t(
                "integrations_setup_guide_desc",
                "Follow these steps to get the credentials this integration needs",
              )}
            </p>
          </div>
          {/* Hidden until we have first-party docs.
          {fullDocsUrl && (
            <Button variant="outline" size="sm" asChild className="shrink-0">
              <a href={fullDocsUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-3.5" />
                {t("integrations_setup_full_guide", "Full guide")}
              </a>
            </Button>
          )}
          */}
        </div>

        {guide.prerequisites.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
              {t("integrations_setup_prerequisites", "Prerequisites")}
            </h3>
            <ul className="space-y-1.5">
              {guide.prerequisites.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-neutral-900">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success-500" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {guide.steps.length > 0 && (
          <ol className="space-y-4">
            {guide.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                  <h4 className="text-sm font-semibold text-neutral-900">{step.title}</h4>
                  <p className="whitespace-pre-line text-sm text-neutral-700">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}
